/**
 * FormikTextarea — Formik-integrated textarea, sibling to FormikInput.
 *
 * Only 2 files use multiline FormikTextField (CommonInterviewComponents.tsx,
 * GeneralForm.tsx), but built per spec for completeness.
 *
 * Migration mapping:
 *   <FormikTextField name="notes" multiline rows={3} />
 *     ↓
 *   <FormikTextarea name="notes" rows={3} />
 */
import {useField} from 'formik'
import React, {forwardRef, useState} from 'react'
import {Textarea} from '../Textarea'
import type {TextareaProps} from '../Textarea'

export interface FormikTextareaProps
  extends Omit<TextareaProps, 'name' | 'value' | 'onChange' | 'onBlur'> {
  /** Formik field name — required */
  name: string
  /** Custom onChange handler — called BEFORE Formik's handler */
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  /** Custom onBlur handler */
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  /** Custom data-testid (defaults to "formik-textarea-{name}") */
  'data-testid'?: string
}

export const FormikTextarea = forwardRef<HTMLTextAreaElement, FormikTextareaProps>(
  (
    {name, onChange: customOnChange, onBlur: customOnBlur, 'data-testid': testId, id, ...rest},
    ref,
  ) => {
    const [field, meta] = useField(name)
    const [resolvedId] = useState(() => id ?? `formik-textarea-${name}`)
    const hasError = meta.touched && Boolean(meta.error)
    const errorText = hasError && meta.error !== 'DoNotShow' ? meta.error : undefined

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      customOnChange?.(e)
      field.onChange(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLTextAreaElement>) => {
      customOnBlur?.(e)
      field.onBlur(e)
    }

    return (
      <Textarea
        ref={ref}
        id={resolvedId}
        name={name}
        value={field.value ?? ''}
        onChange={handleChange}
        onBlur={handleBlur}
        errorText={errorText}
        data-testid={testId ?? `formik-textarea-${name}`}
        aria-describedby={errorText ? `${resolvedId}-error` : undefined}
        {...rest}
      />
    )
  },
)
FormikTextarea.displayName = 'FormikTextarea'

export default FormikTextarea
